import { useEffect, useState } from 'preact/hooks';
import { useI18n } from '../i18n';
import { RequireAdmin } from '../components/RequireAdmin';
import { useAuth } from '../lib/auth';
import {
  listAdminLeagues,
  updateAdminLeague,
  deleteAdminLeague,
  getAdminLeagueLeaderboard,
  rescoreAdminLeague,
  type AdminLeague,
  type AdminLeagueLeaderboard,
  type AdminLeagueUpdate,
  type RescoreResult,
} from '../lib/admin';

// Pretty 4-4-4 form used in the app + invite share. Server stores the raw
// 12-char value, so input is normalised back before any API call.
function formatCode(id: string): string {
  if (id.length !== 12) return id;
  return `${id.slice(0, 4)}-${id.slice(4, 8)}-${id.slice(8, 12)}`;
}

export function AdminLeagues() {
  const { localePath } = useI18n();
  const { accessToken } = useAuth();
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [leagues, setLeagues] = useState<AdminLeague[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [editing, setEditing] = useState<AdminLeague | null>(null);
  const [viewing, setViewing] = useState<AdminLeague | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<AdminLeague | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 250);
    return () => clearTimeout(t);
  }, [search]);

  async function load() {
    if (!accessToken) return;
    setLoading(true);
    setErr(null);
    try {
      const res = await listAdminLeagues(accessToken, debounced);
      setLeagues(res);
    } catch (e: any) {
      setErr(e?.message || `${e}`);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, debounced]);

  async function onDelete(lg: AdminLeague) {
    if (!accessToken) return;
    try {
      await deleteAdminLeague(accessToken, lg.id);
      setConfirmDelete(null);
      load();
    } catch (e: any) {
      setErr(e?.message || `${e}`);
    }
  }

  return (
    <RequireAdmin>
      <div class="admin-page">
        <div class="admin-shell">
          <header class="admin-header">
            <a class="admin-back" href={localePath('/admin')}>← Admin</a>
            <h1>Leagues</h1>
          </header>

          <p class="admin-lede">
            Every personal league across all users. Rules are frozen at
            creation for owners; the edit modal below is the support override
            — use it sparingly. Deleting a league removes all member rows via
            FK cascade.
          </p>

          <div class="admin-filters">
            <input
              type="search"
              placeholder="Search by name…"
              value={search}
              onInput={(e) => setSearch((e.target as HTMLInputElement).value)}
            />
          </div>

          {err && <p class="admin-error">{err}</p>}
          {loading && <p class="admin-lede">Loading…</p>}

          {!loading && leagues.length === 0 && (
            <p class="admin-lede">No leagues yet.</p>
          )}

          {leagues.length > 0 && (
            <table class="admin-table">
              <thead>
                <tr>
                  <th></th>
                  <th>Name</th>
                  <th>Code</th>
                  <th>Members</th>
                  <th>Owner</th>
                  <th>Rules (O / H / A / D / E)</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {leagues.map((lg) => (
                  <tr key={lg.id}>
                    <td style={{ fontSize: 22 }}>{lg.emoji || '·'}</td>
                    <td>{lg.name}</td>
                    <td>
                      <code style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>
                        {formatCode(lg.id)}
                      </code>
                    </td>
                    <td>{lg.members}</td>
                    <td>
                      <code style={{ fontFamily: 'ui-monospace, monospace', fontSize: 11, color: 'rgba(245,237,224,0.55)' }}>
                        {lg.ownerUserId ? lg.ownerUserId.slice(0, 8) : '—'}
                      </code>
                    </td>
                    <td style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>
                      {lg.outcome} / {lg.homeGoals} / {lg.awayGoals} / {lg.diff} / {lg.exact}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <button class="admin-link" onClick={() => setViewing(lg)}>View</button>
                      {' · '}
                      <button class="admin-link" onClick={() => setEditing(lg)}>Edit</button>
                      {' · '}
                      <button class="admin-link" onClick={() => setConfirmDelete(lg)}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {viewing && (
          <ViewModal
            league={viewing}
            onClose={() => setViewing(null)}
          />
        )}

        {editing && (
          <EditModal
            league={editing}
            onClose={() => setEditing(null)}
            onSaved={() => {
              setEditing(null);
              load();
            }}
          />
        )}

        {confirmDelete && (
          <div class="admin-overlay" onClick={() => setConfirmDelete(null)}>
            <div class="admin-modal" onClick={(e) => e.stopPropagation()}>
              <h2>Delete this league?</h2>
              <p>
                <strong>{confirmDelete.emoji} {confirmDelete.name}</strong>
                {' · '}
                <code>{formatCode(confirmDelete.id)}</code>
                {' · '}{confirmDelete.members} members
              </p>
              <p class="admin-lede">
                All member rows and the league row itself are removed. This
                can't be undone.
              </p>
              <div class="admin-modal-actions">
                <button class="admin-link" onClick={() => setConfirmDelete(null)}>
                  Cancel
                </button>
                <button
                  class="admin-primary admin-primary--danger"
                  onClick={() => onDelete(confirmDelete)}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </RequireAdmin>
  );
}

type EditModalProps = {
  league: AdminLeague;
  onClose: () => void;
  onSaved: () => void;
};

function EditModal({ league, onClose, onSaved }: EditModalProps) {
  const { accessToken } = useAuth();
  const [name, setName] = useState(league.name);
  const [emoji, setEmoji] = useState(league.emoji ?? '');
  const [outcome, setOutcome] = useState(String(league.outcome));
  const [homeGoals, setHomeGoals] = useState(String(league.homeGoals));
  const [awayGoals, setAwayGoals] = useState(String(league.awayGoals));
  const [diff, setDiff] = useState(String(league.diff));
  const [exact, setExact] = useState(String(league.exact));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: Event) {
    e.preventDefault();
    if (!accessToken) return;
    setErr(null);
    setSaving(true);
    try {
      const intField = (label: string, raw: string, min: number, max: number) => {
        const n = parseInt(raw, 10);
        if (isNaN(n) || n < min || n > max) {
          throw new Error(`${label}: must be a whole number between ${min} and ${max}.`);
        }
        return n;
      };
      const payload: AdminLeagueUpdate = {
        name: name.trim(),
        emoji: emoji.trim() || undefined,
        outcome: intField('Outcome', outcome, 0, 99),
        homeGoals: intField('Home goals', homeGoals, 0, 99),
        awayGoals: intField('Away goals', awayGoals, 0, 99),
        diff: intField('Diff', diff, 0, 99),
        exact: intField('Exact', exact, 0, 99),
      };
      await updateAdminLeague(accessToken, league.id, payload);
      onSaved();
    } catch (e: any) {
      setErr(e?.message || `${e}`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div class="admin-overlay" onClick={onClose}>
      <div class="admin-modal" onClick={(e) => e.stopPropagation()}>
        <h2>Edit league</h2>
        <p class="admin-lede">
          Support override — owners can't reach this. Saving rule changes
          triggers a full league rescore: existing leaderboards update to the
          new values immediately. Boost is tournament-wide and edited on
          /admin/rules — not here.
        </p>

        <form onSubmit={submit} class="admin-form">
          <label class="admin-field">
            <span>Name</span>
            <input
              type="text"
              value={name}
              maxLength={64}
              onInput={(e) => setName((e.target as HTMLInputElement).value)}
            />
          </label>

          <label class="admin-field">
            <span>Emoji</span>
            <input
              type="text"
              value={emoji}
              maxLength={8}
              placeholder="🍺"
              onInput={(e) => setEmoji((e.target as HTMLInputElement).value)}
            />
          </label>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
            <NumField label="Outcome (1X2)"      value={outcome}     onChange={setOutcome} />
            <NumField label="Home goals correct" value={homeGoals}   onChange={setHomeGoals} />
            <NumField label="Away goals correct" value={awayGoals}   onChange={setAwayGoals} />
            <NumField label="Goal diff"          value={diff}        onChange={setDiff} />
            <NumField label="Exact score"        value={exact}       onChange={setExact} />
          </div>

          {err && <p class="admin-error">{err}</p>}

          <div class="admin-modal-actions">
            <button type="button" class="admin-link" onClick={onClose}>Cancel</button>
            <button type="submit" class="admin-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

type ViewModalProps = {
  league: AdminLeague;
  onClose: () => void;
};

function ViewModal({ league, onClose }: ViewModalProps) {
  const { accessToken } = useAuth();
  const [board, setBoard] = useState<AdminLeagueLeaderboard | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [preview, setPreview] = useState<RescoreResult | null>(null);
  const [working, setWorking] = useState(false);

  async function loadBoard() {
    if (!accessToken) return;
    setLoading(true);
    setErr(null);
    try {
      setBoard(await getAdminLeagueLeaderboard(accessToken, league.id));
    } catch (e: any) {
      setErr(e?.message || `${e}`);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadBoard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, league.id]);

  async function runPreview() {
    if (!accessToken) return;
    setErr(null);
    setWorking(true);
    try {
      setPreview(await rescoreAdminLeague(accessToken, league.id, { dryRun: true }));
    } catch (e: any) {
      setErr(e?.message || `${e}`);
    } finally {
      setWorking(false);
    }
  }

  async function applyRescore() {
    if (!accessToken) return;
    setErr(null);
    setWorking(true);
    try {
      await rescoreAdminLeague(accessToken, league.id, { dryRun: false });
      setPreview(null);
      await loadBoard();
    } catch (e: any) {
      setErr(e?.message || `${e}`);
    } finally {
      setWorking(false);
    }
  }

  return (
    <div class="admin-overlay" onClick={onClose}>
      <div
        class="admin-modal"
        onClick={(e) => e.stopPropagation()}
        style={{ width: 'min(720px, 100%)', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}
      >
        <h2>{league.emoji ? `${league.emoji} ` : ''}{league.name}</h2>
        <p class="admin-lede" style={{ marginBottom: 12 }}>
          <code style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>
            {formatCode(league.id)}
          </code>
          {' · '}{league.members} members
          {' · '}Rules {league.outcome}/{league.homeGoals}/{league.awayGoals}/{league.diff}/{league.exact}
        </p>

        {err && <p class="admin-error">{err}</p>}

        <div style={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>
          {preview ? (
            <PreviewDiff result={preview} />
          ) : loading ? (
            <p class="admin-lede">Loading leaderboard…</p>
          ) : board && board.entries.length > 0 ? (
            <table class="admin-table">
              <thead>
                <tr>
                  <th style={{ width: 40 }}>#</th>
                  <th>Player</th>
                  <th style={{ width: 64, textAlign: 'right' }}>Picks</th>
                  <th style={{ width: 64, textAlign: 'right' }}>Pts</th>
                </tr>
              </thead>
              <tbody>
                {board.entries.map((e) => (
                  <tr key={e.userId}>
                    <td style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>{e.rank}</td>
                    <td>
                      {e.nickname || (
                        <code style={{ fontFamily: 'ui-monospace, monospace', fontSize: 11, color: 'rgba(245,237,224,0.55)' }}>
                          {e.userId.slice(0, 8)}
                        </code>
                      )}
                      {e.canton && (
                        <span style={{ marginLeft: 6, color: 'rgba(245,237,224,0.55)', fontSize: 11 }}>
                          {e.canton}
                        </span>
                      )}
                    </td>
                    <td style={{ textAlign: 'right', fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>{e.picks}</td>
                    <td style={{ textAlign: 'right', fontFamily: 'ui-monospace, monospace', fontSize: 13, fontWeight: 700 }}>{e.pts}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p class="admin-lede">No members yet.</p>
          )}
        </div>

        <div class="admin-modal-actions">
          {preview ? (
            <>
              <button
                class="admin-link"
                onClick={() => setPreview(null)}
                disabled={working}
              >
                Discard
              </button>
              <button
                class="admin-primary"
                onClick={applyRescore}
                disabled={working || preview.predictionsChanged === 0}
              >
                {working ? 'Applying…' : 'Apply rescore'}
              </button>
            </>
          ) : (
            <>
              <button class="admin-link" onClick={onClose}>Close</button>
              <button
                class="admin-primary"
                onClick={runPreview}
                disabled={working}
              >
                {working ? 'Computing…' : 'Force recalculate'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function PreviewDiff({ result }: { result: RescoreResult }) {
  if (result.predictionsChanged === 0) {
    return (
      <p class="admin-lede">
        Recompute matches the stored leaderboard exactly — nothing to apply.
      </p>
    );
  }
  const changed = result.members.filter((m) => m.delta !== 0);
  return (
    <>
      <p class="admin-lede" style={{ marginBottom: 12 }}>
        <strong>{result.membersChanged}</strong> member{result.membersChanged === 1 ? '' : 's'} would change
        {' · '}
        <strong>{result.predictionsChanged}</strong> prediction row{result.predictionsChanged === 1 ? '' : 's'} would update.
      </p>
      <table class="admin-table">
        <thead>
          <tr>
            <th>Player</th>
            <th style={{ width: 80, textAlign: 'right' }}>Old</th>
            <th style={{ width: 80, textAlign: 'right' }}>New</th>
            <th style={{ width: 80, textAlign: 'right' }}>Δ</th>
          </tr>
        </thead>
        <tbody>
          {changed.map((m) => (
            <tr key={m.userId}>
              <td>
                {m.nickname || (
                  <code style={{ fontFamily: 'ui-monospace, monospace', fontSize: 11, color: 'rgba(245,237,224,0.55)' }}>
                    {m.userId.slice(0, 8)}
                  </code>
                )}
              </td>
              <td style={{ textAlign: 'right', fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>{m.oldPts}</td>
              <td style={{ textAlign: 'right', fontFamily: 'ui-monospace, monospace', fontSize: 12, fontWeight: 700 }}>{m.newPts}</td>
              <td
                style={{
                  textAlign: 'right',
                  fontFamily: 'ui-monospace, monospace',
                  fontSize: 12,
                  color: m.delta > 0 ? '#7bc47f' : '#e87b1e',
                }}
              >
                {m.delta > 0 ? '+' : ''}{m.delta}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

function NumField({
  label, value, onChange, max = 99,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  max?: number;
}) {
  return (
    <label class="admin-field">
      <span>{label}</span>
      <input
        type="number"
        min={0}
        max={max}
        value={value}
        onInput={(e) => onChange((e.target as HTMLInputElement).value)}
      />
    </label>
  );
}
